import { useState, useEffect, useCallback, useRef } from 'react';
import { apiClient } from '../api/client';

interface EntranceInfo {
  entranceId: string;
  entranceName: string;
  count: number;
}

interface MonitorAssignment {
  id: string;
  entranceId: string;
  entranceName: string;
}

interface EntryCounts {
  totalCount: number;
  hasActiveSession: boolean;
  byEntrance: EntranceInfo[];
}

interface EntryRecordResponse {
  id: string;
  entranceId: string;
  entranceName: string;
  entranceCount: number;
  totalCount: number;
  recordedAt: string;
}

interface UndoResponse {
  entranceCount: number;
}

interface EntranceListItem {
  id: string;
  eventId: string;
  name: string;
  entryCount: number;
}

interface AssignmentResponse {
  id: string;
  entranceId: string;
  entranceName: string;
  userId: string;
  userName: string;
  isActive: boolean;
  assignedAt: string;
}

export interface UseGateMonitoringResult {
  counts: EntryCounts | null;
  assignment: MonitorAssignment | null;
  entrances: EntranceListItem[];
  loading: boolean;
  error: string;
  tap: () => Promise<void>;
  undo: () => Promise<void>;
  assignEntrance: (entranceId: string) => Promise<void>;
  unassignEntrance: () => Promise<void>;
}

const POLL_INTERVAL_MS = 15_000;

export function useGateMonitoring(eventId: string): UseGateMonitoringResult {
  const [counts, setCounts] = useState<EntryCounts | null>(null);
  const [assignment, setAssignment] = useState<MonitorAssignment | null>(null);
  const [entrances, setEntrances] = useState<EntranceListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchCounts = useCallback(async () => {
    try {
      const data = await apiClient<EntryCounts>(`/gate/events/${eventId}/counts`);
      setCounts(data);

      // Auto-disable: if session is no longer active, clear assignment
      if (!data.hasActiveSession && assignment) {
        // Session stopped — UI will disable tap interface via hasActiveSession flag
      }
    } catch {
      // Retain last valid data on poll failure
    }
  }, [eventId, assignment]);

  const fetchAssignment = useCallback(async () => {
    try {
      const data = await apiClient<AssignmentResponse | { assignment: null }>(`/gate/events/${eventId}/my-assignment`);
      if (data && 'isActive' in data && data.isActive) {
        setAssignment({
          id: (data as AssignmentResponse).id,
          entranceId: (data as AssignmentResponse).entranceId,
          entranceName: (data as AssignmentResponse).entranceName,
        });
      } else {
        setAssignment(null);
      }
    } catch {
      // Retain last state on failure
    }
  }, [eventId]);

  const fetchEntrances = useCallback(async () => {
    try {
      const data = await apiClient<EntranceListItem[]>(`/gate/events/${eventId}/entrances`);
      setEntrances(data);
    } catch {
      // Retain last state on failure
    }
  }, [eventId]);

  // Initial data fetch
  useEffect(() => {
    let cancelled = false;

    const loadInitial = async () => {
      setLoading(true);
      setError('');
      try {
        const [countsData, assignmentData, entrancesData] = await Promise.all([
          apiClient<EntryCounts>(`/gate/events/${eventId}/counts`),
          apiClient<AssignmentResponse | { assignment: null }>(`/gate/events/${eventId}/my-assignment`),
          apiClient<EntranceListItem[]>(`/gate/events/${eventId}/entrances`),
        ]);

        if (cancelled) return;

        setCounts(countsData);
        setEntrances(entrancesData);

        if (assignmentData && 'isActive' in assignmentData && assignmentData.isActive) {
          setAssignment({
            id: (assignmentData as AssignmentResponse).id,
            entranceId: (assignmentData as AssignmentResponse).entranceId,
            entranceName: (assignmentData as AssignmentResponse).entranceName,
          });
        } else {
          setAssignment(null);
        }
      } catch (err: any) {
        if (!cancelled) {
          // Suppress transient/non-actionable errors
          const msg = err?.message || '';
          if (msg.includes('did not match') || msg.includes('Failed to fetch') || msg.includes('Too Many') || msg.includes('Throttl')) {
            setError('');
          } else {
            setError(msg || 'Failed to load gate monitoring data');
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadInitial();

    return () => {
      cancelled = true;
    };
  }, [eventId]);

  // Polling for counts at 10-second intervals
  useEffect(() => {
    intervalRef.current = setInterval(() => {
      fetchCounts();
      fetchAssignment();
    }, POLL_INTERVAL_MS);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [fetchCounts, fetchAssignment]);

  // Tap action with optimistic update
  const tap = useCallback(async () => {
    if (!assignment || !counts?.hasActiveSession) return;

    // Optimistic update
    setCounts((prev) => {
      if (!prev) return prev;
      const updatedByEntrance = prev.byEntrance.map((e) =>
        e.entranceId === assignment.entranceId
          ? { ...e, count: e.count + 1 }
          : e,
      );
      return {
        ...prev,
        totalCount: prev.totalCount + 1,
        byEntrance: updatedByEntrance,
      };
    });

    try {
      const result = await apiClient<EntryRecordResponse>(`/gate/events/${eventId}/entries`, {
        method: 'POST',
      });
      // Update with server-confirmed counts
      setCounts((prev) => {
        if (!prev) return prev;
        const updatedByEntrance = prev.byEntrance.map((e) =>
          e.entranceId === assignment.entranceId
            ? { ...e, count: result.entranceCount }
            : e,
        );
        return {
          ...prev,
          totalCount: result.totalCount,
          byEntrance: updatedByEntrance,
        };
      });
      setError('');
    } catch (err: any) {
      // Revert optimistic update
      setCounts((prev) => {
        if (!prev) return prev;
        const updatedByEntrance = prev.byEntrance.map((e) =>
          e.entranceId === assignment.entranceId
            ? { ...e, count: Math.max(0, e.count - 1) }
            : e,
        );
        // If session ended, update hasActiveSession to disable the UI
        const msg = err?.message || '';
        const sessionEnded = msg.includes('not active') || msg.includes('session');
        return {
          ...prev,
          totalCount: Math.max(0, prev.totalCount - 1),
          byEntrance: updatedByEntrance,
          ...(sessionEnded && { hasActiveSession: false }),
        };
      });
      // Show user-friendly message instead of raw API error
      const msg = err?.message || '';
      if (msg.includes('not active') || msg.includes('session')) {
        setError('');  // Don't show error — the UI will switch to "Session Stopped" state
      } else {
        setError(msg || 'Failed to record entry');
      }
    }
  }, [eventId, assignment, counts?.hasActiveSession]);

  // Undo action with optimistic update
  const undo = useCallback(async () => {
    if (!assignment || !counts?.hasActiveSession) return;

    // Optimistic decrement
    setCounts((prev) => {
      if (!prev) return prev;
      const updatedByEntrance = prev.byEntrance.map((e) =>
        e.entranceId === assignment.entranceId
          ? { ...e, count: Math.max(0, e.count - 1) }
          : e,
      );
      return {
        ...prev,
        totalCount: Math.max(0, prev.totalCount - 1),
        byEntrance: updatedByEntrance,
      };
    });

    try {
      const result = await apiClient<UndoResponse>(`/gate/events/${eventId}/entries/undo`, {
        method: 'DELETE',
      });
      // Update with server-confirmed count
      setCounts((prev) => {
        if (!prev) return prev;
        const updatedByEntrance = prev.byEntrance.map((e) =>
          e.entranceId === assignment.entranceId
            ? { ...e, count: result.entranceCount }
            : e,
        );
        // Recalculate total from byEntrance
        const newTotal = updatedByEntrance.reduce((sum, e) => sum + e.count, 0);
        return {
          ...prev,
          totalCount: newTotal,
          byEntrance: updatedByEntrance,
        };
      });
      setError('');
    } catch (err: any) {
      // Revert optimistic update
      setCounts((prev) => {
        if (!prev) return prev;
        const updatedByEntrance = prev.byEntrance.map((e) =>
          e.entranceId === assignment.entranceId
            ? { ...e, count: e.count + 1 }
            : e,
        );
        return {
          ...prev,
          totalCount: prev.totalCount + 1,
          byEntrance: updatedByEntrance,
        };
      });
      setError(err.message || 'Failed to undo entry');
    }
  }, [eventId, assignment, counts?.hasActiveSession]);

  // Assign entrance action
  const assignEntrance = useCallback(
    async (entranceId: string) => {
      try {
        const result = await apiClient<AssignmentResponse>(
          `/gate/events/${eventId}/entrances/${entranceId}/monitor`,
          { method: 'POST' },
        );
        setAssignment({
          id: result.id,
          entranceId: result.entranceId,
          entranceName: result.entranceName,
        });
        setError('');
        // Refresh counts after assignment
        await fetchCounts();
      } catch (err: any) {
        setError(err.message || 'Failed to assign entrance');
      }
    },
    [eventId, fetchCounts],
  );

  // Unassign from current entrance (stop monitoring)
  const unassignEntrance = useCallback(async () => {
    if (!assignment) return;
    setAssignment(null);
    setError('');
  }, [assignment]);

  return {
    counts,
    assignment,
    entrances,
    loading,
    error,
    tap,
    undo,
    assignEntrance,
    unassignEntrance,
  };
}
